<template>
	<div class="top-performance-slider bg-primary">
		<h3 class="top-performance-slider-title">{{ $t('topPerformance.title') }}</h3>
		
		<slick ref="topPerfomanaceSlick" :options="slickOptions" @afterChange="handleAfterChange" @reInit="handleReInit">
			<div class="top-performance-item" v-for="(item, index) in items" :key="`tp-item-${index}`">
				<router-link :to="{ name: 'single-destination', params: { uuid: item.id} }" class="thumbnail-container">
					<img v-lazy="item.image_url"  :alt="`${item.title} - Thumbnail`">
				</router-link>
				<div class="item-info">
					<div class="category" :class="categoryClass(item)" v-if="item.category">{{ item.category }}</div>
					<h3 class="title" v-if="item.title">
						<router-link :to="{ name: 'single-destination', params: { uuid: item.id} }">
							{{ item.title }}
						</router-link>
					</h3>
					<div class="meta-infos">
						<span class="distance" v-if="item.distance">
							<font-awesome-icon :icon="['far', 'location-arrow']" />
							{{ (item.distance/1000).toFixed(0) + ' km' }}
						</span>
						<span class="likes">
							<font-awesome-icon :icon="['far', 'thumbs-up']" />
							{{ item.likes }}
						</span>
					</div>
				</div>
				<div class="actions-container">
					<button type="button" :class="{ 'button hollow btn-add-to-bookmarks' :true, 'in-bookmarks': inBookmarks(item.id) }" @click="toggleBookmark(item.id)">
						<font-awesome-icon :icon="bookmarkIcon(item.id)"	/>
					</button>
				</div>
			</div>
		</slick>

		<div class="size-indicator">
			<span class="current-slide">{{ selectedSlide }}</span> / <span class="total-slides">{{ this.itemCount }}</span>
		</div>
	</div>
</template>

<script>
	import Slick from 'vue-slick'
	import {getCategoryColorization} from  '@/setup/category-colorization'

	export default{
		name: 'TopPerformanceSlider',
		components: {
			Slick
		},
		props: {
			items: {
				type: Array,
				default: () => {
					return []
				}
			},
			switchedEntities: {
				type: Boolean,
				default: false
			}
		},
		data: () => {
			return {
				slickOptions: {
					slidesToShow: 1,
					slidesToScroll: 1,
					infinite: false,
					arrows: true,
					dots: false,
					rows: false,
					appendArrows: '.top-performance-slider',
					prevArrow: '<button type="button" class="slick-prev"></button>',
					nextArrow: '<button type="button" class="slick-next"></button>',
					fade: true,
					cssEase: 'linear'
				},
				selectedSlide: 1
			}
		},
		computed: {
			itemCount () {
				return this.items.length
			}
		},
		methods: {
			toggleBookmark (id){
				if (this.inBookmarks(id)) {
					this.removeFromBookmarks(id)
				} else {
					this.addToBookmarks(id)
				}
			},
			removeFromBookmarks (id) {
				this.$store.dispatch('removeBookmark', id)
			},
			addToBookmarks (id) {
				this.$store.dispatch('createBookmark', { id: id, type: 'company' })
			},
			inBookmarks (id) {
				return this.$store.getters.getCurrentBookmarks.filter(company => company.id === id).length === 1
			},
			bookmarkIcon (id) {
				if (this.inBookmarks(id)) {
					return ['fas', 'heart']
				} else {
					return ['fal', 'heart']
				}
			},
			handleAfterChange (event, slick, currentSlide) {
				this.selectedSlide = currentSlide + 1
			},
			handleReInit (){
				console.warn('re init tp slider')
			},
			reInit() {
				// Helpful if you have to deal with v-for to update dynamic lists
				this.$refs.topPerfomanaceSlick.reSlick()
			},
			categoryClass(item) {
				let cat = getCategoryColorization(item.category, item.language)
				return 'category-' + item.season + '-' + cat
			}
		},
		updated () {
			// handle re-init if the entities switched, emit event to notify parent when re initalized
			if (this.switchedEntities){
				this.reInit()
				this.$refs.topPerfomanaceSlick.goTo(0)
				this.$emit('tp-slider-reinit')
			}
		},
		mounted () {
			this.reInit()
			this.$refs.topPerfomanaceSlick.goTo(0)
			// console.log(`Mounted top performance slider with ${this.items.length} slides`)
		},
		watch: {
			items: function () {
				this.$refs.topPerfomanaceSlick.goTo(0)
				this.selectedSlide = 1
				
				this.$nextTick(() => {
					this.$refs.topPerfomanaceSlick.reSlick()
				})
			}
		}
	}
</script>

<style lang="scss">
	.top-performance-slider{
		position: relative;
		margin-bottom: .5rem!important;

		.slick-arrow{
			position: absolute;
			top: 50%;
			transform: translate(0, -50%);
			color: $black;
			z-index: 10;
			width: 6rem;
			height: 6rem;
			border-radius: 50%;
			background-color: get-color(secondary);
			cursor: pointer;
			transition: all .2s ease-in-out;

			&.slick-disabled{
				color: $medium-gray;
				background-color: rgba(get-color(secondary), .9);
			}

			&.slick-prev{
				left: -3rem;
				
				&:before{
					content: '\f053';
					font-family: 'Font Awesome 5 Pro';
					font-size: 2rem;
					font-weight: bold;
					margin-left: 2rem;
				}
			}
			&.slick-next{
				right: -3rem;
				
				&:before{
					content: '\f054';
					font-family: 'Font Awesome 5 Pro';
					font-size: 2rem;
					font-weight: bold;
					margin-right: 2rem;
				}
			}
		}
	}
</style>

<style lang="scss" scoped>
	.top-performance-slider{
		@include xy-gutters($gutter-type: padding);
		position: relative;
		width: 100%;
		padding-top: 1rem;
		padding-bottom: 1rem;

		.top-performance-slider-title{
			margin-bottom: 1.5rem;
			font-size: rem-calc(22);
			font-weight: 600;
			color: $white;
		}

		.top-performance-item{
			@include xy-grid();
			color: get-color(primary);
			background-color: $white;
			border-radius: .3rem;

			.thumbnail-container{
				@include xy-cell(12, false, 0);
				position: relative;
				height: rem-calc(250);
				overflow: hidden;
				border-top-left-radius: .3rem;
				border-top-right-radius: .3rem;

				img {
					position: absolute;
					top: 50%;
					transform: translate(0, -50%);
					min-height: rem-calc(250);
					min-width: 100%;
					object-fit: cover;
				}
			}
			.item-info{
				@include xy-cell(10, false, 0);
				@include xy-gutters($gutter-type: padding, $gutter-position: top left bottom right);

				.category{
					margin: 0;
					line-height: 1;
				}
				.title{
					margin-bottom: 1rem;
					font-weight: bold;
				}
				.meta-infos{
					color: $medium-gray;
					.distance{
						padding-right: 1rem;
					}
					.likes{}
				}
			}
			.actions-container{
				@include xy-cell(2, false, 0);
				@include xy-gutters($gutter-type: padding, $gutter-position: top right);
				display: flex;
				justify-content: flex-end;
				align-items: flex-start;

				.btn-add-to-bookmarks{
					display: flex;
					justify-content: center;
					align-items: center;
					width: 2rem;
					height: 2rem;
					margin: 0;
					padding: .25rem;
					border-radius: 20rem;

					svg {
						margin-top: 2px;
					}

					&.in-bookmarks {
						background-color: rgba(get-color(primary), .2);
					}
				}
			}
		}

		.size-indicator{
			text-align: center;
			color: $white;

			.current-slide{
				font-weight: 600;
			}
		}
	}
</style>
